import React, {useMemo, useState} from "react"
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {
    useReactTable,
    getCoreRowModel,
} from '@tanstack/react-table'

import DataTable from "../../components/DataTable";
import WorkgroupsAutocomplete from "../../components/inputs/WorkgroupsAutocomplete";
import StationCol from "../../components/columns/StationCol";
import FiltersBox from "../../components/FiltersBox";
import StationCode from "../../components/columns/StationCode";
import Elevation from "../../components/inputs/Elevation";
import StationVisibilityAutocomplete from "../../components/inputs/StationVisibilityAutocomplete";
import MicroZonesAutocomplete from "../../components/inputs/MicroZonesAutocomplete";

import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import MDBadge from "../../materialDashboard/components/MDBadge";
import ProvinceAutocomplete from "../../components/inputs/ProvinceAutocomplete";
import MunicipalityAutocomplete from "../../components/inputs/MunicipalityAutocomplete";
import StationTypeSelect from "../../components/inputs/StationTypeSelect";
import CurrentlyActiveSelect from "../../components/inputs/CurrentlyActiveSelect";
import AcceptsModelSelect from "../../components/inputs/AcceptsModelSelect";


const StationsTable = (props) => {
    const {
        rows,
        page_count=-1,
        pagination,
        setPagination,
        onGlobalFilterChange,
        onFilterChange,
        sorting,
        onSortingChange,
        map,
        filterProps,
        dataTableId
    } = props;
    const {t} = useTranslation(["stations", "enums"]);

    const FlagIcon = (flag) => {
        return flag ? <Icon color="success">check_circle</Icon> : <Icon color="error">block</Icon>
    }

    const columns = useMemo(
        () => [
            {
                accessorKey: 'station',
                cell: props => <StationCol station={props.row?.original} />,
                header: () => <span>{t("table.station")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "20rem"
                }
            },
            {
                accessorKey: 'code',
                cell: props => <StationCode station={props?.row?.original } />,
                header: () => <span>{t("table.code")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "5rem",
                    align: "center",
                    px: 0,
                    py: 0
                }
            },
            {
                accessorKey: 'elevation',
                cell: info => info.getValue() !== null ? `mt. ${info.getValue()}` : "",
                header: () => <span>{t("table.elevation")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "6rem",
                    align: "center",
                    px: 0,
                    py: 0
                }
            },
            {
                accessorKey: 'locationName',
                cell: info => info.getValue(),
                header: () => <span>{t("table.locationName")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "20rem",
                    px: 0,
                    py: 0
                }
            },
            {
                accessorKey: 'microZones',
                cell: info => info.getValue()?.[0]?.code,
                header: () => <span>{t("table.microZone")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "7rem",
                    align: "center",
                    px: 0,
                    py: 0
                }
            },
            {
                accessorKey: 'acceptMod1',
                cell: info => FlagIcon(info.getValue()),
                header: () => <span>{t("table.acceptMod1")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "4rem",
                    align: "center",
                    px: 0,
                    py: 0
                }
            },
            {
                accessorKey: 'acceptMod23',
                cell: info => FlagIcon(info.getValue()),
                header: () => <span>{t("table.acceptMod23")}</span>,
                enableSorting: true,
                footer: props => props.column.id,
                meta: {
                    width: "4rem",
                    align: "center",
                    px: 0,
                    py: 0
                }
            },
            {
                accessorKey: 'visibility',
                cell: info => t(`enums:stationVisibility.${info.getValue()}`),
                header: () => <span>{t("table.visibility")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "7rem",
                    align: "center"
                }
            },
            {
                accessorKey: 'activeStatus',
                cell: props => <ActiveStatus station={props.row.original} />,
                header: () => <span>{t("table.activeStatus")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    width: "4rem",
                    align: "center"
                }
            },
            {
                accessorKey: 'Actions',
                cell: props => <ActionCol cell={props}/>,
                header: () => <span>{t("table.actions")}</span>,
                enableSorting: false,
                footer: props => props.column.id,
                meta: {
                    align: "right"
                }
            },
        ],
        // When the selected station change rerender the table to apply the change
        []  // eslint-disable-line react-hooks/exhaustive-deps
    )

    const defaultData = useMemo(() => [], [])

    const table = useReactTable({
        data: rows ?? defaultData,
        columns,
        pageCount: page_count ?? -1,
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onGlobalFilterChange: onGlobalFilterChange,
        onSortingChange: onSortingChange,
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,  // rows are expected to be paginated (controlled mode)
        manualSorting: true,  // rows are expected to be sorted (controlled mode)
        // getPaginationRowModel: getPaginationRowModel(), // If only doing manual pagination, you don't need this
        debugTable: false,
        meta: {
            map
        }
    })

    return <DataTable dataTableId={dataTableId}
                      table={table}
                      canSearch
                      showRowColoring={props.showRowColoring}
                      rowColoring={props.rowColoring}
                      setRowColoring={props.setRowColoring}
                      stickyHeader={true}
                      filtersEl={<Filters onChange={onFilterChange} {...filterProps} />}/>
}

const ActionCol = ({cell}) => {
    const navigate = useNavigate();
    const station = cell.row.original;
    const {id} = station;
    const map = cell.table.options?.meta.map;
    const pos = {lat: station.geom.coordinates[1], lng: station.geom.coordinates[0]}

    return <Stack direction="row" spacing={1} justifyContent="flex-end">
        <IconButton onClick={() => map.flyTo(pos)}>
            <Icon fontSize="small">location_searching</Icon>
        </IconButton>
        <IconButton onClick={() => navigate(`/stations/${id}`)}>
            <Icon fontSize="small">edit</Icon>
        </IconButton>
    </Stack>
}

const ActiveStatus = (props) => {
    const isActive = Boolean(props.station.currentlyActive);
    const isActiveContent = isActive ? "active" : "not active";
    const isActiveColor = isActive ? "success" : "error";

    return <MDBadge badgeContent={isActiveContent} color={isActiveColor} variant="gradient" size="sm" />
}

const Filters = ({onChange, initialValues={}}) => {
    const {t} = useTranslation("stations");

    const [selectedWorkgroups, setSelectedWorkgroups] = useState(initialValues.selectedWorkgroups ?? []);
    const [selectedMicroZones, setSelectedMicroZones] = useState([]);
    const [stationType, setStationType] = useState(null);
    const [stationVisibility, setStationVisibility] = useState(null);
    const [elevationGte, setElevationGte] = useState(null);
    const [elevationLte, setElevationLte] = useState(null);
    const [provinceId, setProvinceId] = useState(initialValues.provinceId ?? null);
    const [municipalityId, setMunicipalityId] = useState(initialValues.municipalityId ?? null);
    const [currentlyActive, setCurrentlyActive] = useState(initialValues.currentlyActive ?? 'true');
    const [acceptsModel1, setAcceptsModel1] = useState(initialValues.acceptsModel1 ?? '');
    const [acceptsModel23, setAcceptsModel23] = useState(initialValues.acceptsModel23 ?? '');

    const filters = {
        "workgroup_id__in": selectedWorkgroups.map(w => w.id).join(','),
        "micro_zone_id__in": selectedMicroZones.map(m => m.id).join(','),
        "type": stationType,
        "visibility": stationVisibility,
        "elevation__gte": elevationGte,
        "elevation__lte": elevationLte,
        "province_id": provinceId,
        "municipality_id": municipalityId,
        "currently_active": currentlyActive === "" ? null : currentlyActive,
        "accept_mod1": acceptsModel1 === "" ? null : acceptsModel1,
        "accept_mod23": acceptsModel23 === "" ? null : acceptsModel23,
    }
    const handleOnApplyFilters = () => {
        onChange(filters);
    }

    const handleProvinceChange = (provinceId) => {
        setProvinceId(provinceId);
        setMunicipalityId(null);
    }

    return <FiltersBox onApplyFilters={handleOnApplyFilters}>
        <Grid container columnSpacing={2} rowSpacing={2}>
            <Grid item xs={12} md={3}>
                <WorkgroupsAutocomplete selectedWorkgroups={selectedWorkgroups}
                                        setSelectedWorkgroups={setSelectedWorkgroups}
                                        fullWidth/>
            </Grid>
            <Grid item xs={12} md={3}>
                <MicroZonesAutocomplete selectedMicroZones={selectedMicroZones}
                                        setSelectedMicroZones={setSelectedMicroZones}
                                        microZoneFilter={{workgroup_id__in: filters.workgroup_id__in}}
                                        fullWidth/>
            </Grid>
            <Grid item xs={12} md={3}>
                <StationTypeSelect value={stationType} onChange={(e) => setStationType(e.target.value)} fullWidth/>
            </Grid>
            <Grid item xs={12} md={3}>
                <StationVisibilityAutocomplete setStationVisibility={setStationVisibility}
                                               fullWidth/>
            </Grid>
            <Grid item xs={12} md={3}>
                <Elevation elevation={elevationGte} setElevation={setElevationGte} labelType="gte"/>
            </Grid>
            <Grid item xs={12} md={3}>
                <Elevation elevation={elevationLte} setElevation={setElevationLte} labelType="gte"/>
            </Grid>
            <Grid item xs={12} md={3}>
                <ProvinceAutocomplete name="provinceId"
                                      value={provinceId}
                                      onChange={handleProvinceChange}
                                      MDInputProps={{
                                          name: 'provinceId',
                                          label: t('filters.provinceId'),
                                      }}/>
            </Grid>
            <Grid item xs={12} md={3}>
                <MunicipalityAutocomplete name="municipalityId"
                                          provinceId={provinceId}
                                          value={municipalityId}
                                          onChange={setMunicipalityId}
                                          MDInputProps={{
                                              name: 'municipalityId',
                                              label: t('filters.municipalityId'),
                                          }}/>
            </Grid>
            <Grid item xs={12} md={3}>
                <CurrentlyActiveSelect value={currentlyActive}
                                       onChange={(e) => setCurrentlyActive(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
                <AcceptsModelSelect modelType="model1"
                                    value={acceptsModel1}
                                    onChange={(e) => setAcceptsModel1(e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
                <AcceptsModelSelect modelType="model23"
                                    value={acceptsModel23}
                                    onChange={(e) => setAcceptsModel23(e.target.value)} />
            </Grid>
        </Grid>
    </FiltersBox>
}

export default StationsTable;